import { useMemo } from 'react';

// T270(2)/T161 — the eight filter-dropdown option lists, lifted out of
// DashboardPage.
//
// They sat interleaved with an unrelated effect in the middle of a 2600-line
// component, which is why nobody could see they are one thing: every one of
// them derives an option list from either the server facet (`recStats`, which
// describes the whole pool) or the rows currently loaded. That distinction is
// the interesting part and it was invisible — city and source counts come from
// the server so "ran but matched nothing" reads differently from "has rows",
// while country/remote/seniority/company are derived from the loaded window and
// therefore describe only what is on screen.
//
// THREE sources, not two: `availableSources` also reads the LAST SCAN's
// sourcesQueried, so a source that ran and matched nothing still appears in the
// dropdown with a zero. The page holds those under two separate names, `stats`
// (scans) and `recStats` (recommendation facets), and reading them both from
// one 2600-line scope is how that distinction stayed invisible.
//
// Pure: no state, no effects, no fetch. Only the memos moved.
export function useDashboardFacets({ availableUnfiltered, recStats, scanStats }) {
  // How many available rows each source actually has, straight from the server
  // facet. Drives both the ordering below and the number shown next to each
  // option, so "ran but matched nothing" is visibly different from "has rows".
  // T194 — city counts from the server facet, so the dropdown describes the
  // whole pool rather than the page the dashboard happens to hold.
  const cityCounts = useMemo(() => {
    const m = {};
    for (const c of recStats?.cities ?? []) if (c?.city) m[c.city] = c.count;
    return m;
  }, [recStats?.cities]);

  const availableCities = useMemo(() => {
    const set = new Set(Object.keys(cityCounts));
    for (const r of availableUnfiltered) if (r.job?.cityNormalized) set.add(r.job.cityNormalized);
    return [...set].sort((a, b) => {
      const ca = cityCounts[a] ?? 0;
      const cb = cityCounts[b] ?? 0;
      if (ca !== cb) return cb - ca;
      return a.localeCompare(b);
    });
  }, [cityCounts, availableUnfiltered]);

  const sourceCounts = useMemo(() => {
    const m = {};
    for (const s of recStats?.sources ?? []) if (s?.source) m[s.source] = s.count;
    return m;
  }, [recStats?.sources]);

  // Distinct sources for the filter chip row. Union of:
  //   - sources present in the current recommendations pool, AND
  //   - sources from the last scan's sourcesQueried (so a freshly-added
  //     adapter shows up as a chip even when it returned 0 matched —
  //     operator wants to see "this source ran, produced 0").
  const availableSources = useMemo(() => {
    const set = new Set();
    for (const r of availableUnfiltered) if (r.job?.source) set.add(r.job.source);
    for (const s of scanStats?.lastScan?.sourcesQueried ?? []) if (s?.source) set.add(s.source);
    // The server facet is authoritative: it covers the whole pool, not just the
    // rows this page holds. Without it the dropdown listed 11 of 20 sources,
    // and a manually imported job's source was never among them because its
    // score put it two hundred rows down.
    for (const s of recStats?.sources ?? []) if (s?.source) set.add(s.source);
    // Sources that actually have rows come first, biggest first; the adapters
    // that ran and matched nothing sink to the bottom. The last scan queried 67
    // adapters, so plain alphabetical order buried the useful entries — `import`
    // sat at position 51 of 69 inside a box that shows nine at a time, which
    // reads as "it is not in the list".
    return [...set].sort((a, b) => {
      const ca = sourceCounts[a] ?? 0;
      const cb = sourceCounts[b] ?? 0;
      if (ca !== cb) return cb - ca;
      return a.localeCompare(b);
    });
  }, [availableUnfiltered, scanStats?.lastScan?.sourcesQueried, recStats?.sources, sourceCounts]);

  const availableCountries = useMemo(
    () => [...new Set(availableUnfiltered.map((r) => r.job?.location?.country).filter(Boolean))].sort(),
    [availableUnfiltered],
  );

  // T142 — remote / seniority / company option lists for the new dropdowns.
  const availableRemotes = useMemo(
    () => [...new Set(availableUnfiltered.map((r) => r.job?.location?.remote).filter((v) => v && v !== 'unknown'))].sort(),
    [availableUnfiltered],
  );

  const availableSeniorities = useMemo(() => {
    const order = ['junior', 'mid', 'senior', 'staff', 'principal'];
    const present = new Set(availableUnfiltered.map((r) => r.job?.extracted?.seniority).filter(Boolean));
    // Keep the canonical seniority order, then append anything unexpected.
    return order.filter((s) => present.has(s)).concat([...present].filter((s) => !order.includes(s)));
  }, [availableUnfiltered]);

  const availableCompanies = useMemo(
    () =>
      [...new Set(availableUnfiltered.map((r) => r.job?.company).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b),
      ),
    [availableUnfiltered],
  );

  return {
    cityCounts,
    availableCities,
    sourceCounts,
    availableSources,
    availableCountries,
    availableRemotes,
    availableSeniorities,
    availableCompanies,
  };
}
